/**
 * Review Step Component
 *
 * Step 3 of the assessment wizard - review and submit
 * Features:
 * - Score summary with pass/fail counts
 * - Category score sliders (auto-calculated, overridable)
 * - Overall score display with weighting breakdown
 * - Manager notes textarea
 */

import { useMemo } from 'react';
import {
  CheckCircle2,
  XCircle,
  ShieldAlert,
  TrendingUp,
  Target,
  Shield,
  FileText,
  AlertCircle,
} from 'lucide-react';
import type { SopDetail, SopTaskStep } from '@guild/shared';
import type { AssessmentFormData } from '../NewAssessmentPage';
import { calculateOverallScore } from '../NewAssessmentPage';
import { getScoreColor, getScoreBgWithBorder } from '../../../lib/score-colors';

interface ReviewStepProps {
  formData: AssessmentFormData;
  sop: SopDetail;
  allSteps: Array<SopTaskStep & { sectionTitle: string; taskTitle: string; isSafetyCritical: boolean }>;
  calculatedScores: { compliance: number; efficiency: number; safety: number };
  onCategoryScoreChange: (category: 'compliance' | 'efficiency' | 'safety', value: number) => void;
  onNotesChange: (notes: string) => void;
}


export function ReviewStep({
  formData,
  sop,
  allSteps,
  calculatedScores,
  onCategoryScoreChange,
  onNotesChange,
}: ReviewStepProps) {
  // Calculate summary stats
  const summary = useMemo(() => {
    const scored = Object.values(formData.stepScores);
    const passed = scored.filter(s => s.passed).length;
    const failed = scored.filter(s => !s.passed).length;

    const safetySteps = allSteps.filter(s => s.isSafetyCritical);
    const safetyScored = safetySteps.filter(s => formData.stepScores[s.id]);
    const safetyPassed = safetyScored.filter(s => formData.stepScores[s.id]?.passed).length;
    const safetyFailed = safetyScored.length - safetyPassed;

    return {
      total: allSteps.length,
      scored: scored.length,
      passed,
      failed,
      safetyTotal: safetySteps.length,
      safetyPassed,
      safetyFailed,
    };
  }, [formData.stepScores, allSteps]);

  // Overall score calculation
  const overallScore = calculateOverallScore(
    formData.complianceScore,
    formData.efficiencyScore,
    formData.safetyScore
  );

  // Check if scores have been manually adjusted
  const isManuallyAdjusted = useMemo(() => {
    return (
      formData.complianceScore !== calculatedScores.compliance ||
      formData.efficiencyScore !== calculatedScores.efficiency ||
      formData.safetyScore !== calculatedScores.safety
    );
  }, [formData, calculatedScores]);

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-[#00674a]/5 to-[#c5e8c2]/10">
        <h2 className="text-xl font-bold text-gray-900">Review Assessment</h2>
        <p className="text-sm text-gray-500 mt-1">
          Review scores and add any notes before submitting
        </p>
      </div>

      <div className="p-8 space-y-8">
        {/* Score Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Scored */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-2 text-gray-500 mb-2">
              <FileText className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Scored</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">{summary.scored}</div>
            <div className="text-sm text-gray-500">of {summary.total} steps</div>
          </div>

          {/* Passed */}
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
            <div className="flex items-center gap-2 text-emerald-600 mb-2">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Passed</span>
            </div>
            <div className="text-3xl font-bold text-emerald-700">{summary.passed}</div>
            <div className="text-sm text-emerald-600">steps</div>
          </div>

          {/* Failed */}
          <div className="bg-red-50 rounded-xl p-4 border border-red-100">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <XCircle className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Failed</span>
            </div>
            <div className="text-3xl font-bold text-red-700">{summary.failed}</div>
            <div className="text-sm text-red-600">steps</div>
          </div>

          {/* Safety */}
          <div className={`rounded-xl p-4 border ${
            summary.safetyFailed > 0 ? 'bg-red-50 border-red-100' : 'bg-emerald-50 border-emerald-100'
          }`}>
            <div className={`flex items-center gap-2 mb-2 ${
              summary.safetyFailed > 0 ? 'text-red-600' : 'text-emerald-600'
            }`}>
              <ShieldAlert className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Safety</span>
            </div>
            <div className={`text-3xl font-bold ${
              summary.safetyFailed > 0 ? 'text-red-700' : 'text-emerald-700'
            }`}>
              {summary.safetyPassed}/{summary.safetyTotal}
            </div>
            <div className={`text-sm ${
              summary.safetyFailed > 0 ? 'text-red-600' : 'text-emerald-600'
            }`}>
              critical passed
            </div>
          </div>
        </div>

        {/* Category Scores */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900">Category Scores</h3>
            {isManuallyAdjusted && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">
                <AlertCircle className="w-3.5 h-3.5" />
                Manually adjusted
              </span>
            )}
          </div>

          {/* Compliance */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Target className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Compliance</span>
                  <span className="text-xs text-gray-500 ml-2">(40% weight)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${getScoreColor(formData.complianceScore, 'text')}`}>
                  {formData.complianceScore}
                </span>
                {formData.complianceScore !== calculatedScores.compliance && (
                  <span className="text-xs text-gray-400">
                    (auto: {calculatedScores.compliance})
                  </span>
                )}
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.complianceScore}
              onChange={(e) => onCategoryScoreChange('compliance', parseInt(e.target.value, 10))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 ${formData.complianceScore}%, #e5e7eb ${formData.complianceScore}%)`,
              }}
            />
          </div>

          {/* Efficiency */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#c5e8c2] flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-[#00674a]" />
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Efficiency</span>
                  <span className="text-xs text-gray-500 ml-2">(30% weight)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${getScoreColor(formData.efficiencyScore, 'text')}`}>
                  {formData.efficiencyScore}
                </span>
                {formData.efficiencyScore !== calculatedScores.efficiency && (
                  <span className="text-xs text-gray-400">
                    (auto: {calculatedScores.efficiency})
                  </span>
                )}
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.efficiencyScore}
              onChange={(e) => onCategoryScoreChange('efficiency', parseInt(e.target.value, 10))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #00674a ${formData.efficiencyScore}%, #e5e7eb ${formData.efficiencyScore}%)`,
              }}
            />
          </div>

          {/* Safety */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Safety</span>
                  <span className="text-xs text-gray-500 ml-2">(30% weight)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${getScoreColor(formData.safetyScore, 'text')}`}>
                  {formData.safetyScore}
                </span>
                {formData.safetyScore !== calculatedScores.safety && (
                  <span className="text-xs text-gray-400">
                    (auto: {calculatedScores.safety})
                  </span>
                )}
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.safetyScore}
              onChange={(e) => onCategoryScoreChange('safety', parseInt(e.target.value, 10))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #dc2626 ${formData.safetyScore}%, #e5e7eb ${formData.safetyScore}%)`,
              }}
            />
          </div>
        </div>

        {/* Overall Score */}
        <div className={`rounded-xl p-6 border ${getScoreBgWithBorder(overallScore)}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Overall Score</h3>
              <p className="text-sm text-gray-500 mt-1">
                (Compliance × 0.4) + (Efficiency × 0.3) + (Safety × 0.3)
              </p>
              <p className="text-xs text-gray-400 mt-2">
                ({formData.complianceScore} × 0.4) + ({formData.efficiencyScore} × 0.3) + ({formData.safetyScore} × 0.3) = {overallScore}
              </p>
            </div>
            <div className="text-right">
              <div className={`text-5xl font-bold ${getScoreColor(overallScore, 'text')}`}>
                {overallScore}
              </div>
              <div className="text-sm text-gray-500 mt-1">out of 100</div>
            </div>
          </div>
        </div>

        {/* Manager Notes */}
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-semibold text-gray-700">Manager Notes</span>
            <span className="text-xs text-gray-400 ml-2">(optional, max 5000 characters)</span>
          </label>
          <textarea
            value={formData.managerNotes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Add any additional notes, observations, or recommendations..."
            rows={4}
            maxLength={5000}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 resize-none"
          />
          <div className="text-right text-xs text-gray-400">
            {formData.managerNotes.length} / 5000
          </div>
        </div>

        {/* SOP Info */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00674a] flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900">{sop.title}</p>
              <p className="text-sm text-gray-500">v{sop.version}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
